import { useQuery } from 'data-fetching-client';
import { GET_PUBLIC_GROWTH_PURCHASE_OPTIONS_V2_QUERY } from 'pricing-pages-ui-public-app/__generated__/chirp/com/hubspot/buying/rpc/PublicProductRpc.dfc.new';
import { useMemo } from 'react';
import { useCurrency } from '../../../context/CurrencyContext';
import { isAbortedQueryError } from '../../../utils/isAbortedQueryError';
import { getBrowserLocale } from '../utils/getBrowserLocale';

/**
 * Base hook for querying growth pop purchase options. This should not be used directly,
 * instead use one of the more specific hooks below.
 * @param productApiNames - The product API names to query
 * @returns The products, currency code, error, and loading state
 */
export const useGrowthPopPurchaseOptions = ({
  productsToPurchase,
  skip
}) => {
  const {
    displayedCurrencyCode: currencyCode
  } = useCurrency();
  const {
    browserLocale,
    preferredCountryCode
  } = getBrowserLocale();
  const {
    data,
    error,
    loading
  } = useQuery(GET_PUBLIC_GROWTH_PURCHASE_OPTIONS_V2_QUERY, {
    variables: {
      products: productsToPurchase,
      currencyCode,
      browserLocale,
      preferredCountry: preferredCountryCode
    },
    skip: skip || !currencyCode
  });
  const products = data === null || data === void 0 ? void 0 : data.result.products;
  const filteredError = isAbortedQueryError(error) ? undefined : error;
  return {
    products,
    currencyCode: data === null || data === void 0 ? void 0 : data.result.currencyCode,
    error: filteredError,
    loading
  };
};

/**
 * Returns the one-time fee pricing for a list of products (e.g., onboarding services).
 *
 * @param productApiNames - Array of product API names to get pricing for
 * @param quantity - The quantity for each product (defaults to 1)
 * @returns The pricing data with fullPrice and calculatedPrice for each product
 */
export const useOneTimeFeePricingForProducts = ({
  productApiNames,
  quantity = 1
}) => {
  const productsToPurchase = productApiNames.map(productApiName => ({
    productApiName,
    quantity
  }));
  const {
    products,
    currencyCode,
    error,
    loading
  } = useGrowthPopPurchaseOptions({
    productsToPurchase
  });
  const pricingData = useMemo(() => {
    let totalFullPrice = 0;
    let totalCalculatedPrice = 0;
    productApiNames.forEach(productApiName => {
      var _product$purchaseOpti;
      if (loading) {
        return undefined;
      }
      if (!products) {
        return null;
      }
      const product = products[productApiName];
      const oneTimeFeeOption = product === null || product === void 0 || (_product$purchaseOpti = product.purchaseOptions) === null || _product$purchaseOpti === void 0 ? void 0 : _product$purchaseOpti.ONE_TIME_FEE;
      if (oneTimeFeeOption) {
        var _oneTimeFeeOption$dis;
        const fullPrice = oneTimeFeeOption.priceForCommittedPeriod ? Number(oneTimeFeeOption.priceForCommittedPeriod) : 0;

        // If there are discounts, calculate the discounted price
        let calculatedPrice = fullPrice;
        if (((_oneTimeFeeOption$dis = oneTimeFeeOption.discounts) === null || _oneTimeFeeOption$dis === void 0 ? void 0 : _oneTimeFeeOption$dis.length) > 0) {
          // Apply discounts - sum up all discount amounts
          const totalDiscountAmount = oneTimeFeeOption.discounts.reduce((sum, discount) => {
            const discountAmount = discount.discountedAmountPerPurchase ? Number(discount.discountedAmountPerPurchase) : 0;
            return sum + discountAmount;
          }, 0);
          calculatedPrice = Math.max(0, fullPrice - totalDiscountAmount);
        }
        totalFullPrice += fullPrice;
        totalCalculatedPrice += calculatedPrice;
      }
    });
    return {
      oneTimeFee: {
        fullPrice: totalFullPrice,
        calculatedPrice: totalCalculatedPrice
      }
    };
  }, [loading, products, productApiNames]);
  return {
    pricingData,
    currencyCode,
    loading,
    error
  };
};

/**
 * TERM SELECTOR HOOKS
 */

const filterOutInvalidTerms = ({
  loading,
  terms
}) => {
  return {
    annual: {
      commitment: terms.annual.commitment === 'ANNUAL' || terms.annual.commitment === 'MONTHLY' ? terms.annual.commitment : loading ? undefined : null,
      payment: terms.annual.payment === 'ANNUAL' || terms.annual.payment === 'MONTHLY' ? terms.annual.payment : loading ? undefined : null
    },
    monthly: {
      commitment: terms.monthly.commitment === 'ANNUAL' || terms.monthly.commitment === 'MONTHLY' ? terms.monthly.commitment : loading ? undefined : null,
      payment: terms.monthly.payment === 'ANNUAL' || terms.monthly.payment === 'MONTHLY' ? terms.monthly.payment : loading ? undefined : null
    }
  };
};
const getAvailableTermsForProductApiName = ({
  growthPurchaseOptionsProducts,
  productApiName,
  recurringTermsOnly = false
}) => {
  var _growthPurchaseOption, _growthPurchaseOption2, _growthPurchaseOption3, _growthPurchaseOption4, _growthPurchaseOption5;
  const availableTerms = growthPurchaseOptionsProducts !== null && growthPurchaseOptionsProducts !== void 0 && (_growthPurchaseOption = growthPurchaseOptionsProducts[productApiName]) !== null && _growthPurchaseOption !== void 0 && _growthPurchaseOption.purchaseOptions ? {
    annual: {
      commitment: (growthPurchaseOptionsProducts === null || growthPurchaseOptionsProducts === void 0 || (_growthPurchaseOption2 = growthPurchaseOptionsProducts[productApiName]) === null || _growthPurchaseOption2 === void 0 || (_growthPurchaseOption2 = _growthPurchaseOption2.purchaseOptions) === null || _growthPurchaseOption2 === void 0 || (_growthPurchaseOption2 = _growthPurchaseOption2.ANNUAL) === null || _growthPurchaseOption2 === void 0 ? void 0 : _growthPurchaseOption2.commitmentTerm) || null,
      payment: (growthPurchaseOptionsProducts === null || growthPurchaseOptionsProducts === void 0 || (_growthPurchaseOption3 = growthPurchaseOptionsProducts[productApiName]) === null || _growthPurchaseOption3 === void 0 || (_growthPurchaseOption3 = _growthPurchaseOption3.purchaseOptions) === null || _growthPurchaseOption3 === void 0 || (_growthPurchaseOption3 = _growthPurchaseOption3.ANNUAL) === null || _growthPurchaseOption3 === void 0 ? void 0 : _growthPurchaseOption3.paymentTerm) || null
    },
    monthly: {
      commitment: (growthPurchaseOptionsProducts === null || growthPurchaseOptionsProducts === void 0 || (_growthPurchaseOption4 = growthPurchaseOptionsProducts[productApiName]) === null || _growthPurchaseOption4 === void 0 || (_growthPurchaseOption4 = _growthPurchaseOption4.purchaseOptions) === null || _growthPurchaseOption4 === void 0 || (_growthPurchaseOption4 = _growthPurchaseOption4.MONTHLY) === null || _growthPurchaseOption4 === void 0 ? void 0 : _growthPurchaseOption4.commitmentTerm) || null,
      payment: (growthPurchaseOptionsProducts === null || growthPurchaseOptionsProducts === void 0 || (_growthPurchaseOption5 = growthPurchaseOptionsProducts[productApiName]) === null || _growthPurchaseOption5 === void 0 || (_growthPurchaseOption5 = _growthPurchaseOption5.purchaseOptions) === null || _growthPurchaseOption5 === void 0 || (_growthPurchaseOption5 = _growthPurchaseOption5.MONTHLY) === null || _growthPurchaseOption5 === void 0 ? void 0 : _growthPurchaseOption5.paymentTerm) || null
    }
  } : null;
  const filteredAvailableTerms = recurringTermsOnly && availableTerms ? filterOutInvalidTerms({
    loading: false,
    terms: availableTerms
  }) : availableTerms;
  return recurringTermsOnly ? filteredAvailableTerms : availableTerms;
};
const getAvailableTermForTermLength = ({
  availableTerms,
  termLength
}) => {
  var _availableTerms, _availableTerms2;
  if (!availableTerms) {
    return {
      commitmentTerm: null,
      paymentTerm: null
    };
  }
  return {
    commitmentTerm: (_availableTerms = availableTerms[termLength.toLowerCase()]) === null || _availableTerms === void 0 ? void 0 : _availableTerms.commitment,
    paymentTerm: (_availableTerms2 = availableTerms[termLength.toLowerCase()]) === null || _availableTerms2 === void 0 ? void 0 : _availableTerms2.payment
  };
};
export const useGetAvailableTerms = ({
  productApiName,
  quantity = 1,
  recurringTermsOnly = false
}) => {
  const {
    products,
    error,
    loading,
    currencyCode
  } = useGrowthPopPurchaseOptions({
    productsToPurchase: productApiName ? [{
      productApiName,
      quantity
    }] : [],
    skip: !productApiName
  });
  if (!productApiName || !products || loading || error) {
    return {
      availableTerms: loading ? undefined : null,
      error,
      loading,
      currencyCode
    };
  }
  return {
    availableTerms: getAvailableTermsForProductApiName({
      growthPurchaseOptionsProducts: products,
      productApiName,
      recurringTermsOnly
    }),
    error,
    loading,
    currencyCode
  };
};

/**
 * HOOKS FOR PURCHASE MOTIONS
 */

export const useGrowthPopForPurchaseMotions = ({
  productApiName,
  quantity = 1,
  term = 'ANNUAL'
}) => {
  const {
    availableTerms: availableTermsForRecurringTerms,
    error,
    loading,
    currencyCode
  } = useGetAvailableTerms({
    productApiName,
    quantity,
    recurringTermsOnly: true
  });
  const availableTerms = availableTermsForRecurringTerms;

  // Return null if product is unavailable (availableTerms is null)

  if (!productApiName || !availableTerms) {
    return {
      commitmentTerm: loading ? undefined : null,
      paymentTerm: loading ? undefined : null,
      currencyCode,
      error,
      loading
    };
  }
  const {
    commitmentTerm,
    paymentTerm
  } = getAvailableTermForTermLength({
    availableTerms,
    termLength: term
  });
  return {
    commitmentTerm,
    paymentTerm,
    currencyCode,
    error,
    loading
  };
};